import { CallLogResponse } from "@/dtos/CallLog.dto";

/**
 * Determines Customer and Sales Rep roles based on call direction
 * @param callLog - Call log data with from, to, and direction fields
 * @returns Object with role information and phone numbers
 */
export function getRolesFromCallLog(callLog: { from: string; to: string; direction: string }): {
  salesRepNumber: string;
  customerNumber: string;
  salesRepRole: "Sales Rep";
  customerRole: "Customer";
} {
  const isOutbound = callLog.direction?.startsWith("outbound");

  if (isOutbound) {
    // Outbound: from = Sales Rep, to = Customer
    return {
      salesRepNumber: callLog.from,
      customerNumber: callLog.to,
      salesRepRole: "Sales Rep",
      customerRole: "Customer",
    };
  } else {
    // Inbound: from = Customer, to = Sales Rep
    return {
      customerNumber: callLog.from,
      salesRepNumber: callLog.to,
      customerRole: "Customer",
      salesRepRole: "Sales Rep",
    };
  }
}

/**
 * Parses transcript text with role prefixes (Customer: / Sales Rep:)
 * @param transcriptText - Transcript text with role prefixes
 * @returns Array of parsed transcript segments with role and text
 */
export function parseTranscript(transcriptText: string): Array<{
  role: "Customer" | "Sales Rep";
  text: string;
}> {
  if (!transcriptText) return [];

  // Split on role prefixes while keeping them
  const lines = transcriptText.split(/(?=Customer:|Sales Rep:)/);

  return lines
    .map((line) => {
      const trimmed = line.trim();
      if (!trimmed) return null;

      if (trimmed.startsWith("Customer:")) {
        return {
          role: "Customer" as const,
          text: trimmed.replace(/^Customer:\s*/, "").trim(),
        };
      } else if (trimmed.startsWith("Sales Rep:")) {
        return {
          role: "Sales Rep" as const,
          text: trimmed.replace(/^Sales Rep:\s*/, "").trim(),
        };
      }

      // Handle lines without prefixes (shouldn't happen, but fallback)
      return null;
    })
    .filter((item): item is { role: "Customer" | "Sales Rep"; text: string } => item !== null);
}

/**
 * Type for call caption payload from Socket.IO
 */
export interface CallCaptionPayload {
  callSid: string;
  text: string;
  isFinal: boolean;
  speaker: "caller" | "recipient";
  role: "Customer" | "Sales Rep";
  callFrom?: string;
  callTo?: string;
  callDirection?: string;
}
